import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import InlineError from "../../../shared/ui/Field/InlineError";
import { messageFromError } from "../../../shared/api/drfErrors";

import {
  approveJoinRequest,
  fetchCompany,
  fetchJoinRequests,
  fetchMyCompany,
  rejectJoinRequest,
} from "../api/companyWorkspace";
import {
  canOrdinaryAdminWrite,
  errorCode,
  isTerminalJoinRequestStatus,
  joinRequestStatusLabel,
} from "../lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../lib/companyFocusRevalidation";
import CompanyPageHeader from "../ui/CompanyPageHeader";

function CompanyJoinRequestsManageContent() {
  const { companyId } = useParams();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  const [writesSuspended, setWritesSuspended] = useState(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const releaseBusy = (op) => {
    if (busyOpRef.current === op) {
      busyOpRef.current = null;
      setBusyId(null);
    }
  };

  const load = useCallback(({ background = false } = {}) => {
    if (!companyId) {
      setState("not-found");
      return Promise.resolve();
    }
    const keepVisible = background && stateRef.current === "ready";
    const op = scopeGate.beginRead({ companyId, actorId });
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
    }
    return Promise.all([
      fetchCompany(companyId),
      fetchMyCompany(),
      fetchJoinRequests(companyId, "pending"),
    ])
      .then(([companyData, mine, list]) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        const mineMatches =
          mine?.company?.id != null && sameCompanyId(mine.company.id, companyId)
            ? mine.membership
            : null;
        setCompany(companyData);
        setMembership(mineMatches);
        setAuthorityActorId(actorId);
        if (!canOrdinaryAdminWrite(companyData, mineMatches)) {
          setRows([]);
          setState("denied");
          return;
        }
        setRows(Array.isArray(list) ? list : []);
        revalidationConfirmed = true;
        setActionError((current) =>
          current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
        );
        setState("ready");
      })
      .catch((err) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        const status = err?.response?.status;
        if (keepVisible && status !== 403 && status !== 404) {
          setActionError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
          return;
        }
        setAuthorityActorId(actorId);
        if (status === 403) setState("denied");
        else if (status === 404) setState("not-found");
        else {
          setError(messageFromError(err));
          setState("error");
        }
      })
      .finally(() => {
        settleRevalidationWrites({
          keepVisible: keepVisible,
          confirmed: revalidationConfirmed,
          token: backgroundToken,
          tokenAtStart,
          tokenRef: revalidationTokenRef,
          writesSuspendedRef,
          setWritesSuspended,
        });
      });
  }, [actorId, companyId, scopeGate]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ companyId, actorId });
    busyOpRef.current = null;
    setBusyId(null);
    setActionError(null);
    setRows([]);
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
    };
  }, [companyId, actorId]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready",
    busy: busyId != null,
    onRevalidate: () => load({ background: true }),
  });

  const act = async (row, kind) => {
    if (busyOpRef.current || writesSuspendedRef.current) return;
    if (kind === "approve" && company.status !== "active") return;
    const label = kind === "approve" ? "схвалити" : "відхилити";
    const ok = window.confirm(
      `Дійсно ${label} заявку #${row.id} для «${company.name}»?`,
    );
    if (!ok) return;
    const op = scopeGate.beginMutation({
      companyId,
      actorId,
      resourceId: row.id,
    });
    busyOpRef.current = op;
    setBusyId(row.id);
    setActionError(null);
    try {
      if (kind === "approve") await approveJoinRequest(companyId, row.id);
      else await rejectJoinRequest(companyId, row.id);
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      await load();
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const committed = err?.response?.data?.join_request_status;
      const code = errorCode(err);
      if (isTerminalJoinRequestStatus(committed)) {
        setActionError(
          `Заявку вже оброблено (${joinRequestStatusLabel(committed)}).`,
        );
      } else if (code === "company_enrollment_conflict" || committed === "pending") {
        setActionError(
          "Конфлікт вступу: заявка ще очікує. Перевірте поточне членство або відкритість компанії, потім спробуйте знову.",
        );
      } else {
        setActionError(messageFromError(err));
      }
      await load();
    } finally {
      releaseBusy(op);
    }
  };

  const companyMatchesRoute =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatchesRoute && actorMatches;

  if (
    state === "loading" ||
    (state === "ready" && !authorityMatches)
  ) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження заявок" />
      </div>
    );
  }

  if (state !== "ready") {
    return (
      <div className="feature-company-workspace__state">
        <EmptyState
          icon="fa-solid fa-lock"
          title={
            state === "not-found"
              ? "Не знайдено"
              : state === "error"
                ? "Помилка"
                : "Немає доступу"
          }
          description={
            error ||
            "Керувати заявками може лише активний власник або керівник активної компанії."
          }
          size="md"
        />
        {state === "error" ? (
          <button type="button" className="action-btn" onClick={load}>
            Повторити
          </button>
        ) : null}
      </div>
    );
  }

  if (!authorityMatches) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження заявок" />
      </div>
    );
  }

  return (
    <section className="clients-page feature-company-workspace" id="Csection">
      <div className="container">
        <CompanyPageHeader
          title="Заявки на вступ"
          subtitle="Схвалення або відхилення очікуваних заявок."
          company={company}
          membership={membership}
          companyId={companyId}
          activeNav="join-requests"
        />
        {actionError ? <InlineError>{actionError}</InlineError> : null}
        {rows.length === 0 ? (
          <EmptyState
            icon="fa-solid fa-inbox"
            title="Немає очікуваних заявок"
            description="Нові заявки з’являться тут."
            size="md"
          />
        ) : (
          <ul className="feature-company-workspace__list">
            {rows.map((row) => (
              <li key={row.id} className="feature-company-workspace__card">
                <div className="feature-company-workspace__card-top">
                  <div>
                    <h2>
                      {row.user?.fullname ||
                        row.user?.handle ||
                        row.user?.email ||
                        `Заявка #${row.id}`}
                    </h2>
                    <p>{row.message || "Без повідомлення"}</p>
                  </div>
                  <div className="feature-company-workspace__inline-actions">
                    <button
                      type="button"
                      className="action-btn"
                      disabled={busyId === row.id || company.status !== "active"}
                      onClick={() => act(row, "approve")}
                    >
                      Схвалити
                    </button>
                    <button
                      type="button"
                      className="action-btn action-btn--danger"
                      disabled={busyId === row.id}
                      onClick={() => act(row, "reject")}
                    >
                      Відхилити
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export default CompanyJoinRequestsManageContent;
